import { Component, Input, OnInit } from '@angular/core';
import { ClientWindowSticker } from 'src/app/model/product/windowsticker/client-windowsticker';
import StandardStickerPart from 'src/app/model/product/windowsticker/standard-sticker-part';
import { HeightWidthTuple } from 'src/app/model/util-classes/height-width-tuple';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { SnackService } from 'src/app/services/snack.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { EMPTY_OBJECT } from 'src/globals/globals';
import { WindowStickerSizeGetter } from '../../helper/window-sticker-size-getter';
import { WindowStickerSizeSelectionList } from '../../service/window-sticker-size-selection-list';
import {
  ClientWindowStickerStandardPartFormHelper,
  ShapeOption
} from './helper/client-window-sticker-standard-part-form-helper';

@Component({
  selector: 'client-window-sticker-standard-part-form',
  templateUrl: './client-window-sticker-standard-part-form.component.html',
  styleUrls: ['./client-window-sticker-standard-part-form.component.scss']
})
export class ClientWindowStickerStandardPartFormComponent
  extends SubscriptionBasedComponent
  implements OnInit {
  @Input()
  windowSticker: ClientWindowSticker;

  @Input()
  standardStickerPart: StandardStickerPart;

  @Input()
  windowStickerSizeGetter: WindowStickerSizeGetter;

  SHAPE_OPTIONS = ClientWindowStickerStandardPartFormHelper.SHAPE_OPTIONS;
  SHAPE_OPTIONS_ARRAY =
    ClientWindowStickerStandardPartFormHelper.SHAPE_OPTIONS_ARRAY;
  selectedShapeOption: ShapeOption;

  lyingDownOptions: HeightWidthTuple[] = [];
  squareOptions: HeightWidthTuple[] = [];
  uprightOptions: HeightWidthTuple[] = [];
  selectedHeightWidth: HeightWidthTuple;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private snackService: SnackService
  ) {
    super();
  }

  ngOnInit(): void {
    this.selectedShapeOption = ClientWindowStickerStandardPartFormHelper.getSelectedShapeOption(
      this.standardStickerPart
    );
    this.initializeSubscriptions();
  }

  private initializeSubscriptions(): void {
    this.addSubscription(
      this.windowStickerSizeGetter.getSelectionListSubject(),
      (selectionList: WindowStickerSizeSelectionList | {}) => {
        this.handleSelectionList(selectionList);
        this.selectedHeightWidth = ClientWindowStickerStandardPartFormHelper.getSelectedHeightWidth(
          this.standardStickerPart,
          this.lyingDownOptions,
          this.squareOptions,
          this.uprightOptions
        );
      }
    );

    this.addSubscription(
      this.windowStickerSizeGetter.getMaterialChangedSubject(),
      () => this.handleMaterialChanged()
    );
  }

  private handleSelectionList(
    selectionList: WindowStickerSizeSelectionList | {}
  ): void {
    if (selectionList == null || selectionList === EMPTY_OBJECT) {
      this.lyingDownOptions = [];
      this.squareOptions = [];
      this.uprightOptions = [];
    } else {
      this.lyingDownOptions = (<WindowStickerSizeSelectionList>(
        selectionList
      )).lyingDown;
      this.squareOptions = (<WindowStickerSizeSelectionList>(
        selectionList
      )).square;
      this.uprightOptions = (<WindowStickerSizeSelectionList>(
        selectionList
      )).upright;
    }
  }

  private handleMaterialChanged(): void {
    if (this.windowSticker.foil == null) {
      this.standardStickerPart.productSize = null;
    } else if (this.selectedHeightWidth != null) {
      this.tryGetMatchingWindowStickerSize();
    }
  }

  private async tryGetMatchingWindowStickerSize(): Promise<void> {
    const windowStickerSize = await this.windowStickerSizeGetter.fetchWindowStickerSingleSize(
      this.windowSticker.foil,
      this.selectedHeightWidth.height,
      this.selectedHeightWidth.width
    );
    this.standardStickerPart.productSize = windowStickerSize;

    if (windowStickerSize == null) {
      this.snackService.showWarning(
        `Er bestaat geen standaard formaat van ${this.selectedHeightWidth.display} voor het gekozen materiaal, gelieve ofwel een ander formaat of een ander materiaal te kiezen`
      );
    } else {
      this.projectDisplayObservableService
        .getProductSizeChangedNoSideEffectSubscribable()
        .next();
    }
  }

  setSelectedShapeOption(shapeOption: ShapeOption): void {
    this.selectedShapeOption = shapeOption;
    this.standardStickerPart.productSize = null;
    this.selectedHeightWidth = null;
    this.standardStickerPart.selectedHeightWidth = null;
    this.windowStickerSizeGetter.getFormatsChangedSubject().next();
  }

  setSelectedHeightWidth(heightWidth: HeightWidthTuple): void {
    this.selectedHeightWidth = heightWidth;
    this.standardStickerPart.selectedHeightWidth = heightWidth;
    this.standardStickerPart.requestedProductSize.height = heightWidth.height;
    this.standardStickerPart.requestedProductSize.width = heightWidth.width;
    if (this.windowSticker.foil != null) {
      this.tryGetMatchingWindowStickerSize();
    }
    this.windowStickerSizeGetter.getFormatsChangedSubject().next();
  }

  layoutDescriptionChanged(value: string): void {
    this.standardStickerPart.layoutDescription = value;
  }
}
